import FormCurrencyInputFancy from '@c/forms/controls/FormCurrencyInputFancy';
import FormLabel from '@c/forms/controls/FormLabel';
import BaseModal from '@c/modals/BaseModal';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@ui/Button';
import { ProductDocument } from 'models/product';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import SafeImage from '@ui/SafeImage';
import { formatCurrency, isMobile } from '@util/index';
import { useEffect, useState } from 'react';
import { logError } from '@util/logError';
import MobileDrawer from '@c/drawers/MobileDrawer';
import { getNewPrice } from './PriceDropModal';
import { SmartPricingIcon } from 'i/smart-pricing';

const BASE_PERCENTAGE = 15;

export default function SmartPricingModal({
  open,
  setOpen,
  product,
  updateSmartPricing,
  setPromoteModalIsOpen,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setPromoteModalIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  product: ProductDocument;
  updateSmartPricing: (
    floorPrice: number,
    product: ProductDocument
  ) => Promise<void> | void;
}) {
  const [loading, setLoading] = useState(false);
  const [selectedPercentage, setSelectedPercentage] = useState(BASE_PERCENTAGE);

  const smartPricingModalSchema = z.object({
    floor_price: z
      .number({
        required_error: 'Floor price price is required',
        invalid_type_error: 'Please enter a valid price',
      })
      .gte(1, 'Floor price cannot be lower than $1')
      .refine(
        (value) => value < product.price,
        'Floor price cannot be higher than the actual product price'
      ),
  });

  const {
    control,
    formState: { errors },
    watch,
    setValue,
    handleSubmit,
    reset,
  } = useForm<z.infer<typeof smartPricingModalSchema>>({
    resolver: zodResolver(smartPricingModalSchema),
    defaultValues: {
      floor_price: undefined,
    },
  });

  useEffect(() => {
    if (open) {
      reset({ floor_price: undefined });
      setSelectedPercentage(BASE_PERCENTAGE);
    }
  }, [open, product, reset]);

  const floorPrice = watch('floor_price');

  const onConfirmSmartPricing = async () => {
    try {
      setLoading(true);
      await updateSmartPricing(floorPrice, product);
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  const _isMobile = isMobile();
  const Base = _isMobile ? MobileDrawer : BaseModal;

  const BackButton = () => {
    if (!_isMobile) return null;

    return (
      <button
        className="text-body-md font-semibold text-text-red"
        onClick={() => {
          setOpen(false);
          if (setPromoteModalIsOpen) {
            setPromoteModalIsOpen(true);
          }
        }}
      >
        Back
      </button>
    );
  };

  return (
    <Base
      show={open}
      title="Promote your listing"
      isOpen={open}
      dismiss={() => setOpen(false)}
      headerExtraButton={<BackButton />}
    >
      <form
        className="flex w-full flex-col p-8"
        onSubmit={handleSubmit(onConfirmSmartPricing)}
      >
        <div className="mb-10 flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <SmartPricingIcon className="size-28" />
            <span className="text-heading-h5 font-bold">Smart Pricing</span>
          </div>
          <span className="text-body-md font-medium">
            Smart Pricing will lower your price over time but won&apos;t ever go
            under your minimum.
          </span>
        </div>

        <div className="mb-6 flex w-full items-start gap-5 rounded-xl bg-brand-off-white p-4">
          <SafeImage
            src={product.thumbnail}
            alt={product.title}
            width={56}
            height={56}
            className="aspect-square size-28 rounded-2xl object-cover"
          />
          <div className="flex flex-col gap-2">
            <span className="text-body-md font-semibold">{product.title}</span>
            <span className="text-body-md font-medium text-text-muted">
              Current Price: {formatCurrency(product.price)}
            </span>
          </div>
        </div>

        <div className="mb-6 grid w-full grid-cols-3 gap-x-4">
          {[10, 15, 20].map((percentage) => (
            <div
              className={`rounded-3xl border p-8 ${
                selectedPercentage === percentage
                  ? 'border-brand-secondary bg-[#f9e8e8]'
                  : 'border-[#d9d9d9]  hover:border-brand-secondary'
              } flex cursor-pointer items-center justify-center gap-x-2 text-[1.8rem]`}
              onClick={() => {
                setValue(
                  'floor_price',
                  Number(getNewPrice(product.price, percentage))
                );
                setSelectedPercentage(percentage);
              }}
              key={percentage}
            >
              <div className="flex flex-col items-center">
                <span className="text-heading-h6 font-bold">
                  {formatCurrency(
                    Number(getNewPrice(product.price, percentage))
                  )}
                </span>

                <span className="text-body-md font-semibold text-text-red">
                  {percentage}% OFF
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-6 flex items-center justify-center gap-8">
          <div className="h-px w-full bg-brand-lighter-gray" />

          <span className="text-body-md font-semibold">or</span>

          <div className="h-px w-full bg-brand-lighter-gray" />
        </div>

        <div className="mb-16 flex w-full">
          <FormLabel
            value="Set floor price"
            required
            errorMessage={errors.floor_price?.message}
          >
            <Controller
              control={control}
              name="floor_price"
              render={({ field }) => (
                <div className="flex w-full justify-center">
                  <div className="my-4 w-full">
                    <FormCurrencyInputFancy
                      placeholder="0.00"
                      error={!!errors?.floor_price}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                      }}
                      onBlur={field.onBlur}
                      value={field.value?.toString() ?? ''}
                    />
                  </div>
                </div>
              )}
            />
          </FormLabel>
        </div>
        <Button
          text={loading ? 'Loading...' : 'Confirm'}
          type="secondary"
          className="w-full"
          buttonType="submit"
        />
      </form>
    </Base>
  );
}
